/**
 * ESCHER FALLS — AdMob の実装。`rewardedAd.ts` の差し口に差し込む中身。
 *
 * **リワード動画だけを使う。** バナーもインタースティシャルも入れない
 * （理由は docs/MONETIZATION.md §1）。
 *
 * ## テスト広告 ID を既定にしてある
 * 本物の広告ユニット ID は AdMob アカウントを作らないと得られないが、
 * **Google が公開しているテスト用 ID は誰でも使える。**
 * だから実装と動作確認は、アカウントが無くても最後まで通せる。
 *
 * **本番の ID に差し替えるまで、収益は 1 円も発生しない。**
 * 差し替え忘れは静かに起きる（テスト広告もちゃんと表示されるので気づかない）ため、
 * `usingTestAds()` で外から確かめられるようにしてある。
 *
 * ## ATT（トラッキング許可）
 * 初回に一度だけ確認ダイアログを出す。**断られても広告は出る**——
 * 追跡なしの広告になるだけで、単価が下がる。断った人を締め出してはいけない。
 */

import { Capacitor } from '@capacitor/core';
import { setRewardedAdProvider, type RewardedAdProvider } from './rewardedAd';

/**
 * Google 公式のテスト用リワード広告ユニット（iOS）。
 * https://developers.google.com/admob/ios/test-ads に載っている公開値。
 */
const TEST_REWARDED_ID = 'ca-app-pub-3940256099942544/1712485313';

/**
 * 本番の広告ユニット ID。空にするとテスト広告に戻る（収益は発生しない）。
 *
 * **これは秘密ではない。** アプリに埋め込まれて配布されるので、
 * 誰でも取り出せる。環境変数やキーチェーンに隠す意味は無い。
 */
const PRODUCTION_REWARDED_ID: string = 'ca-app-pub-2627222368508836/3488394807';

/**
 * 自分の端末を「テスト端末」として登録する ID。
 *
 * **本番 ID に切り替えた今、開発機にも本物の広告が出る。**
 * それを自分で見たり押したりすると Google から無効なトラフィックと見なされ、
 * 最悪 AdMob アカウントごと停止される。**手を動かして確かめる端末は
 * 必ずここに登録すること。**
 *
 * ID は実機で一度起動すると Xcode のコンソールに出る:
 *   `To get test ads on this device, set: ...testDeviceIdentifiers = @[ @"XXXX" ]`
 * その `XXXX` をここへ足す。登録した端末にはテスト広告が出るので、
 * 押しても課金元に迷惑がかからない。
 */
const TEST_DEVICE_IDS: readonly string[] = [];

const adUnitId = PRODUCTION_REWARDED_ID || TEST_REWARDED_ID;

/** テスト広告のままか。**提出前に必ず false であることを確かめること。** */
export function usingTestAds(): boolean {
  return PRODUCTION_REWARDED_ID === '';
}

let loaded = false;

/**
 * AdMob を差し込む。**起動時に一度だけ呼ぶ。**
 * 失敗しても投げない——広告が出ないだけで、遊びは最後まで成立する。
 */
export async function registerAdMob(): Promise<void> {
  if (Capacitor.getPlatform() !== 'ios') return;

  try {
    const { AdMob, RewardAdPluginEvents } = await import('@capacitor-community/admob');

    // ## 同意管理（UMP）— **広告 SDK の初期化より先に置くこと**
    // Google は「同意を取ってから Mobile Ads SDK を初期化する」順序を求める。
    // 逆にすると、同意の無いまま SDK が動き出しうる。
    // **EEA・英国・スイスでは、パーソナライズ広告に利用者の同意が要る。**
    // EU 27か国は配信から外したが、**ノルウェー・アイスランド（EEA）と
    // 英国・スイスは残っている。** そこへ同意なしで出してはいけない。
    //
    // 対象外の国では `NOT_REQUIRED` が返り、**何も起きない。**
    // 日本の利用者には一切見えないので、体験は変わらない。
    //
    // **失敗しても広告の初期化は続ける。** 同意が取れなければ
    // パーソナライズなしの広告になるだけで、遊びは止めない。
    try {
      const info = await AdMob.requestConsentInfo();
      if (info.status === 'REQUIRED' && info.isConsentFormAvailable === true) {
        await AdMob.showConsentForm();
      }
    } catch {
      // 同意の仕組みが使えない環境でも、広告そのものは出せる。
    }

    await AdMob.initialize({
      // **`initializeForTesting` は `testingDevices` を有効にするだけの旗**で、
      // 端末 ID を渡さなければ何もしない。以前はここに `usingTestAds()` を
      // 入れていたが、保険が要るのは本番 ID のときなので向きが逆だった。
      initializeForTesting: TEST_DEVICE_IDS.length > 0,
      testingDevices: [...TEST_DEVICE_IDS],
    });

    // ATT。**断られても続ける。** 追跡なしの広告になるだけ。
    try {
      const status = await AdMob.trackingAuthorizationStatus();
      if (status.status === 'notDetermined') {
        await AdMob.requestTrackingAuthorization();
      }
    } catch {
      // ATT が使えない環境でも広告自体は出る。
    }

    // 見終わったかどうかは**イベントで**判る。`showRewardVideoAd` の解決は
    // 「閉じた」であって「最後まで見た」ではないので、そこで渡すと
    // 途中で閉じた人にも見返りが行く。
    let earned = false;
    void AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
      earned = true;
    });

    const prepare = async (): Promise<void> => {
      try {
        await AdMob.prepareRewardVideoAd({ adId: adUnitId });
        loaded = true;
      } catch {
        loaded = false;
      }
    };
    await prepare();

    const provider: RewardedAdProvider = {
      isReady: () => loaded,
      show: async () => {
        if (!loaded) return false;
        earned = false;
        loaded = false;
        try {
          await AdMob.showRewardVideoAd();
        } catch {
          void prepare();
          return false;
        }
        // 次に備えて即座に仕込み直す。**見たあとに毎回読み込み待ちが挟まると
        // 「もう一度見る」が押せない時間が生まれる。**
        void prepare();
        return earned;
      },
    };
    setRewardedAdProvider(provider);
  } catch {
    // プラグインが無い／初期化に失敗した。広告の入口は出ないままになる。
  }
}
